import { Controller } from "@hotwired/stimulus";
import { gsap, Power4, CSSPlugin, ScrollToPlugin } from "gsap/all";

gsap.registerPlugin(CSSPlugin, ScrollToPlugin);

export default class extends Controller {
  static targets = ["title", "button", "overlay", "iframe", "close", "player"];
  static values = {
    provider: { type: String, default: "youtube" }, // "youtube" | "vimeo"
    videoId: String,
    muted: { type: Boolean, default: true },
    loop: { type: Boolean, default: false },
  };

  connect() {
    this._wantPlayWithSound = false;
    this._playerReady = false;
    this._originalRect = null;
    this._originalScrollY = null;
    this._placeholder = null;
    this._init();
  }

  disconnect() {
    if (this.providerValue === "youtube") {
      this.player?.destroy?.();
    } else {
      this.player?.unload?.().catch(() => {});
    }

    this._placeholder?.remove();
    this._placeholder = null;
    document.body.style.overflow = "";
  }

  async _init() {
    const provider = this.providerValue;
    const id = this.videoIdValue;
    if (!id) return;

    if (provider === "youtube") {
      const YT = await loadYouTubeApiOnce();
      const playerVars = {
        autoplay: 1,
        mute: this.mutedValue ? 1 : 0,
        controls: 0,
        rel: 0,
        modestbranding: 1,
        playsinline: 1,
        origin: window.location.origin,
        ...(this.hasStartValue ? { start: this.startValue } : {}),
      };
      // Spécificité YouTube pour boucler une seule vidéo :
      // il faut loop=1 + playlist=<id>
      if (this.loopValue) Object.assign(playerVars, { loop: 1, playlist: id });

      this.player = new YT.Player(this.playerTarget, {
        videoId: id,
        playerVars,
        events: { onReady: () => this._onReady() },
      });
    } else {
      const Vimeo = await loadVimeoApiOnce();
      this.player = new Vimeo.Player(this.playerTarget, {
        id,
        autoplay: true,
        muted: this.mutedValue,
        loop: this.loopValue,
        controls: false,
        byline: false,
        title: false,
        portrait: false,
        dnt: true,
        playsinline: true,
        ...(this.hasStartValue ? { start: this.startValue } : {}),
      });
      this.player.on("loaded", () => this._onReady());
    }
  }

  _onReady() {
    this._playerReady = true;
    this._silentAutoplay().finally(() => {
      // Si jamais l'utilisateur a cliqué sur le bouton play avant que le
      // player ne soit prêt, on a stocké l'info pour ouvrir la vidéo en grand
      // une fois le player prêt
      if (this._wantPlayWithSound) this.expand();
    });
  }

  // Démarrage de la vidéo sans le son
  async _silentAutoplay() {
    try {
      if (this.providerValue === "youtube") {
        try {
          this.player.mute();
        } catch {
          /* no action */
        }
        try {
          this.player.playVideo();
        } catch {
          /* no action */
        }
      } else {
        try {
          await this.player.setMuted(true);
        } catch {
          /* no action */
        }
        try {
          await this.player.play();
        } catch {
          /* no action */
        }
      }
    } catch {
      /* no action */
    }
  }

  // Activation du son
  async _activateWithSound() {
    try {
      if (this.providerValue === "youtube") {
        try {
          this.player.unMute();
        } catch {
          /* no action */
        }
        try {
          this.player.playVideo();
        } catch {
          /* no action */
        }
      } else {
        try {
          await this.player.setMuted(false);
        } catch {
          /* no action */
        }
        try {
          await this.player.play();
        } catch {
          /* no action */
        }
      }
    } catch {
      /* no action */
    }
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("pointer-events-none", "opacity-0");
    }
  }

  // Arret de la vidéo
  _stopVideo() {
    if (this.providerValue === "youtube") {
      this.player.pauseVideo();
    } else {
      this.player.pause();
    }
  }

  // Ouverture de la vidéo en grand et activation du son
  expand() {
    if (!this._playerReady) {
      this._wantPlayWithSound = true;
      return;
    }
    this._activateWithSound();

    const rect = this.element.getBoundingClientRect();
    this._originalRect = rect;
    this._originalScrollY = window.scrollY;

    const computedStyle = window.getComputedStyle(this.element);
    this._placeholder = document.createElement("div");
    this._placeholder.style.height = `${rect.height}px`;
    this._placeholder.style.width = `${rect.width}px`;
    this._placeholder.style.marginTop = computedStyle.marginTop;
    this._placeholder.style.marginBottom = computedStyle.marginBottom;
    this._placeholder.style.marginLeft = computedStyle.marginLeft;
    this._placeholder.style.marginRight = computedStyle.marginRight;
    this._placeholder.style.flexShrink = "0";
    this.element.parentNode.insertBefore(this._placeholder, this.element);

    document.body.style.overflow = "hidden";

    this.element.style.position = "fixed";
    this.element.style.top = `${rect.top}px`;
    this.element.style.left = `${rect.left}px`;
    this.element.style.width = `${rect.width}px`;
    this.element.style.height = `${rect.height}px`;
    this.element.style.margin = "0";

    gsap.to(this.closeTarget, {
      opacity: 1,
      y: 0,
      duration: 0.4,
      ease: Power4.easeOut,
    });

    if (this.hasTitleTarget) {
      gsap.to(this.titleTarget, {
        opacity: 0,
        y: -30,
        pointerEvents: "none",
        duration: 0.6,
        ease: Power4.easeOut,
      });
    }

    gsap.to(this.buttonTarget, {
      opacity: 0,
      y: -30,
      pointerEvents: "none",
      duration: 0.6,
      ease: Power4.easeOut,
    });

    gsap.to(this.overlayTarget, {
      opacity: 0,
      duration: 0.4,
      ease: Power4.easeOut,
    });

    gsap.to(this.element, {
      ...this._computeExpandedSize(),
      duration: 1.3,
      ease: Power4.easeOut,
    });

    this.element.classList.add("z-max");
  }

  // Calcule la taille cible de la vidéo ouverte. Sous 1024px, on contraint
  // au ratio 16/9 pour éviter qu'elle s'étire verticalement en portrait.
  _computeExpandedSize() {
    const isMobile = window.innerWidth < 1024;
    if (!isMobile) {
      return { top: "10vh", left: "5vw", width: "90vw", height: "80vh" };
    }

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const maxWidth = vw * 0.9;
    const maxHeight = vh * 0.8;
    const aspectRatio = 16 / 9;

    let width = maxWidth;
    let height = width / aspectRatio;
    if (height > maxHeight) {
      height = maxHeight;
      width = height * aspectRatio;
    }

    return {
      top: (vh - height) / 2,
      left: (vw - width) / 2,
      width,
      height,
    };
  }

  // Fermeture de la vidéo (réduction a son design d'origine) et mise sur pause
  close() {
    this._stopVideo();

    const targetTop = this._originalRect.top + this._originalScrollY - window.scrollY;

    gsap.to(this.element, {
      top: `${targetTop}px`,
      left: `${this._originalRect.left}px`,
      width: `${this._originalRect.width}px`,
      height: `${this._originalRect.height}px`,
      duration: 0.8,
      ease: Power4.easeOut,
      onComplete: () => {
        this.element.style.position = "";
        this.element.style.top = "";
        this.element.style.left = "";
        this.element.style.width = "";
        this.element.style.height = "";
        this.element.style.margin = "";

        this._placeholder?.remove();
        this._placeholder = null;

        document.body.style.overflow = "";
      },
    });

    if (this.hasTitleTarget) {
      gsap.to(this.titleTarget, {
        opacity: 1,
        y: 0,
        pointerEvents: "auto",
        duration: 0.4,
        ease: Power4.easeOut,
      });
    }

    gsap.to(this.buttonTarget, {
      opacity: 1,
      y: 0,
      pointerEvents: "auto",
      duration: 0.4,
      ease: Power4.easeOut,
    });

    gsap.to(this.overlayTarget, {
      opacity: 0.2,
      duration: 0.4,
      ease: Power4.easeOut,
    });

    gsap.to(this.closeTarget, {
      opacity: 0,
      y: -30,
      duration: 0.4,
      ease: Power4.easeOut,
    });

    this.element.classList.remove("z-max");
  }
}

let ytApiReadyPromise;
let vimeoApiReadyPromise;

// Chargement de l'API youtube et vimeo en dehors du controller afin de ne le
// charger qu'une seule fois même s'il y a plusieurs instances de vidéos sur une page
function loadYouTubeApiOnce() {
  if (ytApiReadyPromise) return ytApiReadyPromise;

  ytApiReadyPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve(window.YT);

    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);

    window.onYouTubeIframeAPIReady = () => resolve(window.YT);
  });

  return ytApiReadyPromise;
}

function loadVimeoApiOnce() {
  if (vimeoApiReadyPromise) return vimeoApiReadyPromise;
  vimeoApiReadyPromise = new Promise((resolve) => {
    if (window.Vimeo?.Player) return resolve(window.Vimeo);
    const s = document.createElement("script");
    s.src = "https://player.vimeo.com/api/player.js";
    s.async = true;
    s.onload = () => resolve(window.Vimeo);
    document.head.appendChild(s);
  });
  return vimeoApiReadyPromise;
}
